function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo } from 'react';
import { compact } from 'lodash';
import Decimal from 'decimal.js';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Spinner, Typography, WithInfoIcon } from 'seagull';
import { routes } from "../../../constants";
import GenericError from "../../../components/GenericError/GenericError";
import CustomLegend from "../charts/CustomLegend/CustomLegend";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import { selectors } from "../../../store/state-selectors";
import { DateRange } from "../../Reports/components/types";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { useIoContext } from "../../../contexts";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import { getLastXMonths } from "../../../utils/date.utils";
import ConditionalWrapper from "../../../components/ConditionalWrapper/ConditionalWrapper";
import GraphWidgetTopCard from "../GraphWidget/GraphWidgetTopCard";
import { toDatestampArray } from "../../../models/time";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import { formatNumericUSDate, utils } from "../../../utils";
import { useStore, useStoreActions } from "../../../contexts";
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import { endOfMonth, format, isSameMonth, startOfQuarter, startOfYear } from 'date-fns';
import { commonBarChartProps, commonBarProps, commonCartesianGridProps, commonXAxisProps } from "../charts/commonChartProps";
import { RevenueStatus, HistoricRevenueModel } from "../../../models/HistoricRevenueModel";
import { colorsMap, getLegendItems, labelMap, revenueStatusOrder } from "./RevenueWidget.utils";
import styles from "./RevenueWidget.module.scss";
const title = 'Revenue';
const stackId = 'a';
const TopCards = (historicRevenueStats, loading) => compact([/*#__PURE__*/React.createElement(TopCard, {
  key: "earned-this-year",
  testid: "revenue-stats-earned-this-year",
  title: "Earned this year",
  titleInfo: "The total revenue earned so far this year, including payments recorded as paid externally.",
  primaryText: historicRevenueStats.totalThisYear(),
  linkTo: "".concat(routes.revenueReport, "?dateRange=").concat(formatNumericUSDate(startOfYear(new Date())), ",").concat(formatNumericUSDate(endOfMonth(new Date())), "&dateRangeOption=").concat(DateRange.CurrentYear),
  loading: loading
}), /*#__PURE__*/React.createElement(TopCard, {
  key: "expected-this-year",
  testid: "revenue-stats-expected-this-year",
  title: "Expected this year",
  titleInfo: "The total revenue expected for this year, including the revenue earned this year and the revenue from scheduled invoices for the rest of the year.",
  primaryText: historicRevenueStats.totalExpectedThisYear(),
  linkTo: routes.futureInvoicesReport,
  loading: loading
})]);
export function getXAxisText(props) {
  const date = props.payload.value;
  return isSameMonth(startOfQuarter(date), date) ? format(date, 'MMM') : '';
}
function getTooltipItems(payload) {
  if (!payload || !payload.length) return;
  const historicRevenueModel = payload[0].payload;
  return revenueStatusOrder.filter(status => historicRevenueModel[status].totalAmount.gt(0)).map(status => ({
    label: "".concat(utils.formatDollarAmount(historicRevenueModel[status].totalAmount), " ").concat(labelMap[status]),
    color: colorsMap[status]
  }));
}
function getTitle(_label, payload) {
  if (!payload || !payload.length) return '';
  const historicRevenueModel = payload[0].payload;
  return format(historicRevenueModel.date, 'MMMM');
}
function HistoricRevenueChart(_ref) {
  let {
    data,
    testid,
    isTestMode
  } = _ref;
  const barChartProps = isTestMode ? {
    height: 100,
    width: 100
  } : {};
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": testid,
    className: styles.chart
  }, /*#__PURE__*/React.createElement(ConditionalWrapper, {
    condition: !isTestMode,
    wrapper: children => /*#__PURE__*/React.createElement(ResponsiveContainer, null, children)
  }, /*#__PURE__*/React.createElement(BarChart, _extends({}, commonBarChartProps, {
    data: data,
    barCategoryGap: '10%'
  }, barChartProps, {
    margin: {
      top: 20,
      left: 16,
      right: 16
    }
  }), /*#__PURE__*/React.createElement(CartesianGrid, commonCartesianGridProps), /*#__PURE__*/React.createElement(Legend, {
    content: /*#__PURE__*/React.createElement(CustomLegend, {
      testid: "".concat(testid, "-legend"),
      items: getLegendItems(data)
    })
  }), /*#__PURE__*/React.createElement(XAxis, _extends({}, commonXAxisProps, {
    dataKey: item => item.date,
    height: 49,
    tick: /*#__PURE__*/React.createElement(CustomXAxisTick, {
      tickHeight: 30,
      getText: getXAxisText
    })
  })), /*#__PURE__*/React.createElement(YAxis, {
    width: 70,
    axisLine: false,
    tickLine: false,
    fontSize: 12,
    tickFormatter: value => value === 0 ? '' : utils.formatDollarAmount(value)
  }), /*#__PURE__*/React.createElement(Tooltip, {
    cursor: false,
    isAnimationActive: false,
    content: /*#__PURE__*/React.createElement(CustomTooltip, {
      skin: "dark",
      testid: "".concat(testid, "-tooltip"),
      getTitle: getTitle,
      getItems: getTooltipItems
    })
  }), revenueStatusOrder.map(status => /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    key: status,
    dataKey: item => item[status].totalAmount.toNumber(),
    stackId: stackId,
    fill: colorsMap[status],
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      getHasRadius: item => {
        const statusIndex = revenueStatusOrder.indexOf(status);
        const statusesAbove = revenueStatusOrder.slice(statusIndex + 1);
        return !statusesAbove.some(s => item[s].totalAmount.gt(0));
      }
    })
  }))))));
}
const PlaceholderChart = props => {
  const {
    getNow
  } = useIoContext();
  const placeholderData = getLastXMonths(getNow(), 12).map(date => ({
    date,
    [RevenueStatus.Paid]: {
      date: toDatestampArray(date),
      totalAmount: new Decimal(4000)
    },
    [RevenueStatus.Expected]: {
      date: toDatestampArray(date),
      totalAmount: new Decimal(1000)
    },
    [RevenueStatus.PaidExternally]: {
      date: toDatestampArray(date),
      totalAmount: new Decimal(4000)
    }
  }));
  return /*#__PURE__*/React.createElement(HistoricRevenueChart, _extends({
    testid: "revenue-chart-placeholder",
    data: placeholderData
  }, props));
};
const TopCard = _ref2 => {
  let {
    title,
    testid,
    linkTo,
    success,
    loading,
    titleInfo = '',
    primaryText
  } = _ref2;
  return /*#__PURE__*/React.createElement(GraphWidgetTopCard, {
    linkTo: linkTo
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": loading ? "".concat(testid, "-loading") : testid,
    className: styles.topCard
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(WithInfoIcon, {
    showInfo: !!titleInfo,
    info: titleInfo
  }, /*#__PURE__*/React.createElement(Typography, {
    skin: success ? 'success' : 'standard',
    variant: "paragraph1",
    ellipsis: true
  }, title))), /*#__PURE__*/React.createElement(Typography, {
    weight: "normal",
    variant: "h5",
    "data-testid": testid && "".concat(testid, "-primary")
  }, loading ? /*#__PURE__*/React.createElement(Spinner, {
    size: 30
  }) : primaryText)));
};
const RevenueWidgetContent = props => {
  const {
    loading,
    historicRevenueStats,
    payouts,
    isTestMode,
    loadingOrEmptyBg
  } = props;
  const {
    getNow
  } = useIoContext();
  const data = useMemo(() => historicRevenueStats.chartData(getNow()), [historicRevenueStats]);
  return /*#__PURE__*/React.createElement(GraphWidgetLayout, {
    title: title,
    loading: loading,
    bottomHeight: "l"
  }, TopCards(historicRevenueStats, loading), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
    empty: !payouts.hasEverHadPaidPayout() && !historicRevenueStats.hasRevenueThisYear() && !historicRevenueStats.hasExpectedRevenue(),
    emptyMsg: "Here you will view your revenue over time",
    "data-testid": "revenue-stats",
    loadingOrEmptyBg: loadingOrEmptyBg
  }, /*#__PURE__*/React.createElement(HistoricRevenueChart, {
    testid: "revenue-stats-chart",
    data: data,
    isTestMode: isTestMode
  })));
};
const RevenueWidget = _ref3 => {
  let {
    isTestMode
  } = _ref3;
  const {
    fetchRevenueWidgetStats,
    fetchPayoutsSummary
  } = useStoreActions();
  const historicRevenueStats = useStore(state => state.historicRevenueStats.data);
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const payouts = useStore(state => state.payoutsSummary.data);
  const initialData = useMemo(() => historicRevenueStats && payouts ? {
    payouts,
    historicRevenueStats
  } : null, [historicRevenueStats, payouts]);
  const loadingOrEmptyBg = () => /*#__PURE__*/React.createElement(PlaceholderChart, {
    isTestMode: isTestMode
  });
  const handleFetchChartData = async () => {
    const [payouts, historicRevenueStats] = await Promise.all([fetchPayoutsSummary(), fetchRevenueWidgetStats()]);
    return {
      payouts,
      historicRevenueStats
    };
  };
  if (isMemberUser) return null;
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: handleFetchChartData,
    initialData: initialData,
    renderChild: _ref4 => {
      let {
        data: {
          payouts,
          historicRevenueStats
        },
        loading
      } = _ref4;
      return /*#__PURE__*/React.createElement(RevenueWidgetContent, {
        loading: loading,
        payouts: payouts,
        isTestMode: isTestMode,
        historicRevenueStats: historicRevenueStats,
        loadingOrEmptyBg: loadingOrEmptyBg
      });
    },
    renderLoader: () => /*#__PURE__*/React.createElement(GraphWidgetLayout, {
      title: title,
      loading: true,
      bottomHeight: "l"
    }, TopCards(new HistoricRevenueModel([], [], []), true), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
      loading: true,
      loadingMsg: "Loading data...",
      loadingOrEmptyBg: loadingOrEmptyBg
    })),
    renderError: () => /*#__PURE__*/React.createElement(GenericError, {
      testid: "widget-error"
    })
  });
};
export default RevenueWidget;